<script setup lang="ts">
import { VueFinalModal } from 'vue-final-modal';
import { computed, ref } from 'vue';
import { useQRCode } from '@vueuse/integrations/useQRCode';
import { copyToClipboard } from '~/utilities/copy-to-clipboard';
import pyscriptApi from '~/utilities/pyscript-api';
import IconCopy from '~icons/mdi/content-copy';
import IconLinkedIn from '~icons/bxl/linkedin';
import IconTwitter from '~icons/bxl/twitter';
import IconClose from '~icons/mdi/close';
import IconExternalLink from '~icons/ooui/link-external-ltr';

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: boolean): void;
  (e: 'close'): void;
}>();

interface Props {
  projectId: string;
  codeUrl: string;
  siteUrl: string;
}

const qrcode = useQRCode(props.siteUrl, {
  width: 260,
  margin: 4,
});
const hasCopiedSiteUrl = ref(false);
const hasCopiedCodeUrl = ref(false);

/**
 * @see https://developer.twitter.com/en/docs/twitter-for-websites/tweet-button/guides/web-intent
 */
const shareTwitter = computed(() => {
  const hashtags = 'python,pyscript';
  const text = `Check out what I created via pyscript.com!\n\n${props.siteUrl}\n\n`;
  return `https://twitter.com/intent/tweet?${encodeURI(`hashtags=${hashtags}&text=${text}`)}`;
});

/**
 * @see https://learn.microsoft.com/en-us/linkedin/consumer/integrations/self-serve/plugins/share-plugin
 */
const shareLinkedIn = computed(() => {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURI(props.siteUrl)}`;
});

function onCopyUrl(text: string, type: 'site' | 'code') {
  onShareClick('url');
  copyToClipboard(text);

  if (type === 'site') {
    hasCopiedSiteUrl.value = true;
    setTimeout(() => (hasCopiedSiteUrl.value = false), 2000);
  }

  if (type === 'code') {
    hasCopiedCodeUrl.value = true;
    setTimeout(() => (hasCopiedCodeUrl.value = false), 2000);
  }
}

function onShareClick(via: 'url' | 'linkedin' | 'twitter') {
  pyscriptApi.shareProject(props.projectId, via);
}
</script>

<template>
  <VueFinalModal
    class="flex items-center justify-center"
    content-class="bg-white dark:bg-gray-675 rounded py-7 px-9 relative max-w-[450px] w-full mx-3 max-h-[90vh] overflow-y-auto custom-scrollbar"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @update:model-value="(val) => emit('update:modelValue', val)"
  >
    <h2 class="font-dm-sans text-3xl font-bold">Share</h2>

    <hr class="mb-6 mt-2" />

    <section class="flex items-start text-sm">
      <button class="flex-1" @click="() => onCopyUrl(siteUrl, 'site')">
        <div class="circle-btn text-2xl">
          <IconCopy aria-hidden="true" />
        </div>
        {{ hasCopiedSiteUrl ? 'Copied!' : 'Copy Site Link' }}
      </button>

      <button class="flex-1" @click="() => onCopyUrl(codeUrl, 'code')">
        <div class="circle-btn text-2xl">
          <IconCopy aria-hidden="true" />
        </div>
        {{ hasCopiedCodeUrl ? 'Copied!' : 'Copy Code Link' }}
      </button>

      <a
        :href="shareLinkedIn"
        class="block flex-1 text-center"
        title="Share on LinkedIn"
        target="_blank"
        rel="noopener noreferrer"
        @click="onShareClick('linkedin')"
      >
        <div class="circle-btn text-3xl">
          <IconLinkedIn aria-hidden="true" />
        </div>
        Share on LinkedIn <IconExternalLink class="inline-block align-middle text-[8px]" />
      </a>

      <a
        :href="shareTwitter"
        class="block flex-1 text-center"
        title="Share on Twitter"
        target="_blank"
        rel="noopener noreferrer"
        @click="onShareClick('twitter')"
      >
        <div class="circle-btn text-3xl">
          <IconTwitter aria-hidden="true" />
        </div>
        Share on Twitter <IconExternalLink class="inline-block align-middle text-[8px]" />
      </a>
    </section>

    <section class="flex flex-col items-center">
      <img :src="qrcode" alt="QR Code" />
      <div class="max-w-xs text-center">
        <span
          class="rounded border border-new-gray-200 bg-new-gray-50 px-1 text-xs text-new-gray-800"
          >{{ props.siteUrl }}</span
        >
      </div>
    </section>

    <button title="Close" class="absolute right-4 top-4 p-1 text-lg" @click="emit('close')">
      <IconClose aria-hidden="true" />
    </button>
  </VueFinalModal>
</template>

<style scoped lang="postcss">
.circle-btn {
  @apply mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-new-gray-100/80 text-new-gray-700 transition-colors hover:bg-new-gray-100 dark:text-gray-675;
}
</style>
